// TOEFL iBT Mock 1 — Listening (2026 format, multistage adaptive)
//
// Task types:
//   response     – Listen and Choose a Response: hear one line, pick the best reply.
//   conversation – Listen to a Conversation (campus / everyday life).
//   announcement – Listen to an Announcement (campus or public notice).
//   talk         – Listen to an Academic Talk (short lecture).
//
// Each recording plays once. Questions appear after it finishes.
// gender ("m" / "f") picks the synthesized voice for each line.

export const MODULE_SECONDS = { module1: 15 * 60, module2: 12 * 60 };

export const TASK_LABELS = {
  response: "Choose a Response",
  conversation: "Conversation",
  announcement: "Announcement",
  talk: "Academic Talk",
};

const W = (text) => ({ speaker: "Woman", gender: "f", text });
const M = (text) => ({ speaker: "Man", gender: "m", text });

const response = (id, line, options, correct) => ({
  id,
  type: "response",
  lines: [line],
  questions: [{ question: "Choose the best response.", options, correct }],
});

export const listeningModules = {
  module1: [
    response("m1-r1", W("Do you know if the bookstore is still open?"), [
      "Yes, I bought two books there.",
      "I think it closes at six.",
      "It's on the second floor.",
      "I'd rather read the novel first.",
    ], 1),
    response("m1-r2", M("Would you mind if I borrowed your notes from Tuesday's lecture?"), [
      "Not at all. I'll bring them tomorrow.",
      "The lecture was on Tuesday.",
      "I didn't mind the lecture.",
      "Yes, I took a lot of notes.",
    ], 0),
    response("m1-r3", W("I can't believe the bus is late again."), [
      "I believe it was a bus.",
      "It arrived at the station.",
      "Maybe we should start leaving earlier.",
      "Again? I just took the train.",
    ], 2),
    response("m1-r4", M("Have you decided which elective you're taking next term?"), [
      "I took it last term.",
      "The elective was difficult.",
      "Yes, I decided to go home.",
      "I'm still choosing between art and economics.",
    ], 3),
    response("m1-r5", W("Why don't we meet at the café instead of the library?"), [
      "Good idea. I could use a coffee.",
      "Because the library is closed.",
      "I don't know why we met.",
      "The café is next to the bank.",
    ], 0),
    response("m1-r6", M("The printer on this floor is out of paper."), [
      "I printed it yesterday.",
      "There's another one by the elevator.",
      "This floor is very clean.",
      "The paper was due on Friday.",
    ], 1),
    response("m1-r7", W("How long did it take you to finish the lab report?"), [
      "It was about chemistry.",
      "The lab is down the hall.",
      "Most of the weekend, actually.",
      "It was ten pages long.",
    ], 2),
    response("m1-r8", M("Didn't you say you'd already registered for the conference?"), [
      "The conference is in April.",
      "Yes, I said that it was interesting.",
      "No, I've never been there.",
      "I meant to, but the website was down.",
    ], 3),
    {
      id: "m1-conv",
      type: "conversation",
      title: "Housing Office",
      lines: [
        M("Hi, I'm in room 214 in Baker Hall. The heater in my room hasn't been working since Sunday."),
        W("I'm sorry to hear that. Did you submit a maintenance request online?"),
        M("I tried, but the form kept giving me an error when I clicked submit."),
        W("Ah, a few students have mentioned that. The system is being updated this week. I'll enter the request for you right now."),
        M("Thanks. Do you know when someone might come by?"),
        W("Heating problems are a priority, so a technician should be there by tomorrow morning. In the meantime, you can pick up a portable heater from the front desk."),
      ],
      questions: [
        {
          question: "Why does the man go to the housing office?",
          options: [
            "To request a different room",
            "To report a problem with his heater",
            "To complain about a noisy neighbor",
            "To pay for a maintenance fee",
          ],
          correct: 1,
        },
        {
          question: "What does the woman suggest the man do until the repair is made?",
          options: [
            "Stay in a friend's room",
            "Submit the online form again",
            "Borrow a heater from the front desk",
            "Call the technician directly",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "m1-ann",
      type: "announcement",
      title: "Library Announcement",
      lines: [
        W("Attention, library users. Beginning next Monday, the second floor of the main library will be closed for three weeks while new study rooms are built. During this time, the quiet study area will move to the ground floor reading room. Books from the second-floor collection can still be requested at the circulation desk, and they'll be ready for pick-up within one hour. We apologize for the inconvenience and thank you for your patience."),
      ],
      questions: [
        {
          question: "What is the announcement mainly about?",
          options: [
            "New borrowing limits at the library",
            "A temporary change caused by construction",
            "The library's new opening hours",
            "A book sale on the second floor",
          ],
          correct: 1,
        },
        {
          question: "How can students get books from the second-floor collection?",
          options: [
            "By asking for them at the circulation desk",
            "By using the stairs at the back of the building",
            "By ordering them online for home delivery",
            "By waiting until the work is finished",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "m1-talk",
      type: "talk",
      title: "Biology: Monarch Butterfly Migration",
      lines: [
        M("Today let's look at one of the most remarkable migrations in the animal world: the monarch butterfly. Every autumn, millions of monarchs in eastern North America fly up to four thousand kilometers to forests in central Mexico. What makes this so puzzling is that no single butterfly ever completes the round trip. The monarchs that fly south in the fall are several generations removed from the ones that left Mexico the previous spring. So how do they know where to go? Researchers have found that monarchs use the position of the sun as a compass, and they correct for the sun's movement across the sky using an internal clock located in their antennae. There's also evidence that they can sense Earth's magnetic field, which may help on cloudy days. Unfortunately, monarch numbers have dropped sharply in recent decades, largely because milkweed, the only plant their caterpillars can eat, has disappeared from much of their breeding range."),
      ],
      questions: [
        {
          question: "What is the talk mainly about?",
          options: [
            "How monarch butterflies find their way during migration",
            "Why monarchs prefer forests in Mexico",
            "How caterpillars turn into butterflies",
            "The life span of different butterfly species",
          ],
          correct: 0,
        },
        {
          question: "According to the professor, what is puzzling about the monarch migration?",
          options: [
            "The butterflies fly only at night.",
            "No individual butterfly makes the whole round trip.",
            "The butterflies travel in very small groups.",
            "The route changes every year.",
          ],
          correct: 1,
        },
        {
          question: "What role do the monarchs' antennae play in navigation?",
          options: [
            "They detect the smell of milkweed.",
            "They sense changes in temperature.",
            "They contain a clock that helps the butterflies use the sun.",
            "They help the butterflies see in cloudy weather.",
          ],
          correct: 2,
        },
        {
          question: "Why does the professor mention milkweed?",
          options: [
            "To explain a major reason for the decline in monarchs",
            "To describe what adult monarchs eat in Mexico",
            "To give an example of a plant that uses magnetism",
            "To show where monarchs spend the winter",
          ],
          correct: 0,
        },
      ],
    },
  ],

  // ── Harder Module 2 ──────────────────────────────────────────
  upper: [
    response("up-r1", M("I was hoping to get your feedback on my draft before Friday."), [
      "Friday works better for the deadline.",
      "Send it over tonight and I'll take a look.",
      "I hope the draft was finished.",
      "My feedback was positive on Friday.",
    ], 1),
    response("up-r2", W("You're not still thinking about switching majors, are you?"), [
      "I switched the lights off already.",
      "My major is in the science building.",
      "Actually, I've already met with an advisor about it.",
      "No, I'm still thinking it's a major.",
    ], 2),
    response("up-r3", M("Wasn't the field trip supposed to be next week?"), [
      "It got moved up because of the weather forecast.",
      "The field is next to the stadium.",
      "Yes, we tripped on the way there.",
      "I'm supposed to go next semester.",
    ], 0),
    response("up-r4", W("I'd sign up for the workshop if it weren't on a Saturday."), [
      "The workshop was held last Saturday.",
      "I didn't know you worked there.",
      "Sign here at the bottom, please.",
      "They're offering a weekday session too, I think.",
    ], 3),
    response("up-r5", M("That presentation went on a lot longer than I expected."), [
      "I expected you to present it.",
      "Tell me about it. I nearly missed my next class.",
      "It's longer than the other building.",
      "The presentation is going on now.",
    ], 1),
    {
      id: "up-conv",
      type: "conversation",
      title: "Office Hours",
      lines: [
        W("Professor Lee, do you have a minute? It's about my research paper on renewable energy."),
        M("Sure, Maya. How's it going?"),
        W("Honestly, not great. My topic was solar power in developing countries, but I'm finding that most of the data is out of date."),
        M("That's a common problem. Have you thought about narrowing the focus? Instead of developing countries in general, you could look at one country that publishes recent figures, like Kenya."),
        W("Kenya... I did see some recent government reports on off-grid solar there."),
        M("There you go. A narrower case study is usually more convincing anyway. Just send me a revised outline by next Wednesday so I can approve the change."),
      ],
      questions: [
        {
          question: "What problem does the woman have with her paper?",
          options: [
            "She cannot decide between two topics.",
            "She cannot find current information on her topic.",
            "Her paper is longer than the limit.",
            "She missed the deadline for her outline.",
          ],
          correct: 1,
        },
        {
          question: "What does the professor imply about narrowing the topic?",
          options: [
            "It will make the paper harder to finish.",
            "It should be approved by another professor.",
            "It is likely to make the paper stronger.",
            "It is only necessary for short papers.",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "up-talk",
      type: "talk",
      title: "Economics: The Anchoring Effect",
      lines: [
        W("In traditional economic models, people make decisions by carefully weighing costs and benefits. But behavioral economists have shown that our judgments are often shaped by irrelevant information. One of the best-known examples is the anchoring effect. In a classic experiment, participants spun a wheel that landed on a random number, and then estimated how many African countries belong to the United Nations. Those who saw a high number on the wheel gave significantly higher estimates than those who saw a low number, even though they knew the number was random. The first number they encountered served as an anchor, and their estimates didn't move far enough away from it. Retailers take advantage of this all the time. When a store displays an original price next to a sale price, the original price acts as an anchor, making the sale price seem like a bargain, whether or not anyone ever paid the original price. Interestingly, studies suggest that even experts, such as real estate agents valuing a house, are not immune to anchoring."),
      ],
      questions: [
        {
          question: "What is the main purpose of the talk?",
          options: [
            "To explain how a mental shortcut can distort judgments",
            "To compare traditional and modern retail pricing",
            "To describe how the United Nations was formed",
            "To argue that experts make better decisions",
          ],
          correct: 0,
        },
        {
          question: "Why does the professor describe the experiment with the wheel?",
          options: [
            "To show that people are good at estimating numbers",
            "To illustrate that a random number influenced people's estimates",
            "To explain how the participants were selected",
            "To prove that participants did not know the number was random",
          ],
          correct: 1,
        },
        {
          question: "According to the professor, how do retailers use anchoring?",
          options: [
            "By lowering prices at the end of the day",
            "By showing a higher original price next to the sale price",
            "By selling products with round-number prices",
            "By asking customers to estimate prices",
          ],
          correct: 1,
        },
        {
          question: "What does the professor imply about real estate agents?",
          options: [
            "They avoid anchoring because of their training.",
            "They set prices using random numbers.",
            "Their judgments can be affected by anchors too.",
            "They are the main subject of anchoring research.",
          ],
          correct: 2,
        },
      ],
    },
  ],

  // ── Easier Module 2 ──────────────────────────────────────────
  lower: [
    response("low-r1", W("What time does the next train leave?"), [
      "At a quarter past ten.",
      "It leaves from the station.",
      "I trained for two hours.",
      "Yes, it's the next one.",
    ], 0),
    response("low-r2", M("Can you help me carry these boxes?"), [
      "The boxes are brown.",
      "Sure, where do they go?",
      "I carried it yesterday.",
      "No, I don't have a car.",
    ], 1),
    response("low-r3", W("Where did you put my umbrella?"), [
      "It's raining outside.",
      "I bought it last week.",
      "It's next to the front door.",
      "Yes, I put it on.",
    ], 2),
    response("low-r4", M("Is this seat taken?"), [
      "I took the bus today.",
      "The seats are blue.",
      "I'm sitting down.",
      "No, go ahead.",
    ], 3),
    response("low-r5", W("How was your weekend?"), [
      "Really relaxing, thanks.",
      "It's on Saturday.",
      "I'll go next weekend.",
      "For two days.",
    ], 0),
    {
      id: "low-ann",
      type: "announcement",
      title: "Sports Center Announcement",
      lines: [
        M("Good afternoon, everyone. This is a reminder that the swimming pool at the sports center will close early today, at four o'clock, for cleaning. The gym and the tennis courts will stay open until nine p.m. as usual. Also, starting next week, yoga classes will be held on Tuesday and Thursday evenings. You can sign up at the front desk. Thank you."),
      ],
      questions: [
        {
          question: "What will happen at four o'clock today?",
          options: ["The gym will close.", "The pool will close.", "A yoga class will start.", "The tennis courts will open."],
          correct: 1,
        },
        {
          question: "How can people join the yoga classes?",
          options: [
            "By calling the sports center",
            "By coming on Tuesday morning",
            "By signing up at the front desk",
            "By sending an email",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "low-talk",
      type: "talk",
      title: "History: The Invention of Paper",
      lines: [
        W("Today we're going to talk about paper. Before paper was invented, people wrote on many different materials, such as clay, animal skins, and silk. Silk was nice to write on, but it was very expensive. Around the year one hundred and five, a Chinese official named Cai Lun found a cheaper way to make a writing surface. He mixed tree bark, old cloth, and water, pressed the mixture flat, and let it dry. The result was light, cheap, and easy to write on. For hundreds of years, papermaking stayed a secret in China. Later, the knowledge spread to the Middle East and then to Europe. Because paper was cheap, more people could own books and letters, and information spread much faster."),
      ],
      questions: [
        {
          question: "What is the talk mainly about?",
          options: ["How silk is made", "The history of paper", "Famous Chinese officials", "Old European books"],
          correct: 1,
        },
        {
          question: "What was the problem with writing on silk?",
          options: ["It was too heavy.", "It was too expensive.", "It was hard to find in China.", "Ink did not stay on it."],
          correct: 1,
        },
        {
          question: "Which material did Cai Lun use to make paper?",
          options: ["Clay", "Animal skins", "Tree bark", "Glass"],
          correct: 2,
        },
        {
          question: "According to the professor, what happened because paper was cheap?",
          options: [
            "Silk became more popular.",
            "More people could own books and letters.",
            "China stopped making paper.",
            "Writing became more difficult.",
          ],
          correct: 1,
        },
      ],
    },
  ],
};
